import React, { memo, useMemo } from "react";
import { News } from "@/types";
import LazyImageArticleCard from "@/components/articles/LazyImageArticleCard";
import SmartLink from "@/components/ui/SmartLink";
import SimpleNewsCarousel from "@/components/ui/SimpleNewsCarousel";
import InfiniteNewsGrid from "@/components/news/InfiniteNewsGrid";
import { NewsType } from "@/stores/unified-news-store";
import { NewsErrorBoundary } from "@/components/ui/ErrorBoundary";
import {
  useLatestNews,
  usePopularNews,
  useHotNews,
  useViralNews,
  useCelebrityNews,
  useMostSeeNews,
  useNewsByCategory,
  useInfiniteNews,
  useInfiniteNewsByCategory,
} from "@/hooks/useNews";

interface DynamicNewsSectionProps {
  title?: string;
  type?: NewsType;
  categorySlug?: string;
  locationSlug?: string | null;
  sortBy?: "latest" | "popular" | "views" | "trending" | "comments";
  // Layout system - describes container layout
  layout?: "single" | "double" | "triple" | "quad" | "list" | "carousel";
  // Card variant - describes individual card appearance
  cardVariant?: "hero" | "row" | "compact" | "sidebar";
  maxItems?: number;
  showViewAll?: boolean;
  showLoadMoreButton?: boolean;
  autoLoadOnScroll?: boolean;
  showEndIndicator?: boolean;
  useInfiniteScroll?: boolean;
  badge?: {
    text: string;
    color: string;
  };
  icon?: string;
  className?: string;
  pageSize?: number;
  carouselOptions?: {
    itemsPerView?: number;
    autoPlay?: boolean;
    autoPlayDelay?: number;
    showArrows?: boolean;
    showDots?: boolean;
    infinite?: boolean;
  };
  // Backward compatibility
  variant?: "grid" | "list" | "mixed" | "horizontal";
}

// Enhanced hook for fetching news data using optimized hooks
const useNewsData = (
  type: NewsType,
  categorySlug?: string,
  locationSlug?: string | null,
  sortBy: string = "latest",
  limit: number = 20,
) => {
  // Debug logging
  console.log("DynamicNewsSection useNewsData:", {
    type,
    categorySlug,
    locationSlug,
    sortBy,
    limit,
    hasCategorySlug: !!categorySlug,
  });

  // If categorySlug is provided, use category-specific fetching
  const categoryQuery = useNewsByCategory({
    categorySlug: categorySlug || "",
    perPage: limit,
    locationSlug: locationSlug || undefined,
    sortBy: sortBy as any,
    enabled: !!categorySlug && categorySlug !== "uncategorized",
  });

  // Type-specific queries
  const latestQuery = useLatestNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled:
      (!categorySlug || categorySlug === "uncategorized") && type === "latest",
  });

  const popularQuery = usePopularNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled: !categorySlug && type === "popular",
  });

  const hotQuery = useHotNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled: !categorySlug && type === "hot",
  });

  const viralQuery = useViralNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled: !categorySlug && type === "viral",
  });

  const celebrityQuery = useCelebrityNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled: !categorySlug && type === "celebrity",
  });

  const mostSeeQuery = useMostSeeNews({
    perPage: limit,
    locationSlug: locationSlug || undefined,
    enabled: !categorySlug && type === "mostsee",
  });

  // Return the appropriate query based on the parameters
  if (categorySlug && categorySlug !== "uncategorized") {
    console.log("Using category query for:", categorySlug);
    return categoryQuery;
  }

  console.log("Using type-specific query for:", type);
  switch (type) {
    case "latest":
      return latestQuery;
    case "popular":
      return popularQuery;
    case "hot":
      return hotQuery;
    case "viral":
      return viralQuery;
    case "celebrity":
      return celebrityQuery;
    case "mostsee":
      return mostSeeQuery;
    default:
      return latestQuery;
  }
};

const DynamicNewsSection: React.FC<DynamicNewsSectionProps> = memo(
  ({
    title,
    type = "latest",
    categorySlug,
    locationSlug,
    sortBy = "latest",
    layout = "double",
    cardVariant = "compact",
    maxItems,
    showViewAll = true,
    showLoadMoreButton = true,
    autoLoadOnScroll = false,
    showEndIndicator = false,
    useInfiniteScroll = false,
    badge,
    icon,
    className = "",
    pageSize = 20,
    carouselOptions = {
      itemsPerView: 3,
      autoPlay: false,
      autoPlayDelay: 5000,
      showArrows: true,
      showDots: false,
      infinite: true,
    },
    // Backward compatibility
    variant,
  }) => {
    // Backward compatibility: convert old variant to new layout/cardVariant
    const resolvedLayout = useMemo(() => {
      if (variant) {
        switch (variant) {
          case "grid":
            return "double";
          case "list":
            return "list";
          case "mixed":
            return "double";
          case "horizontal":
            return "carousel";
          default:
            return layout;
        }
      }
      return layout;
    }, [variant, layout]);

    const resolvedCardVariant = useMemo(() => {
      if (variant) {
        switch (variant) {
          case "grid":
            return "compact";
          case "list":
            return "row";
          case "mixed":
            return "compact";
          case "horizontal":
            return "compact";
          default:
            return cardVariant;
        }
      }
      return cardVariant;
    }, [variant, cardVariant]);

    // Determine news type based on category or explicit type
    const newsType: NewsType = useMemo(() => {
      if (type) return type;
      if (categorySlug === "viral") return "viral";
      if (categorySlug === "selebriti" || categorySlug === "celebrity")
        return "celebrity";
      if (sortBy === "popular") return "popular";
      if (sortBy === "views") return "mostsee";
      return "latest";
    }, [type, categorySlug, sortBy]);

    // Use TanStack Query for data fetching
    const {
      data: news = [],
      isLoading,
      isError,
      error,
    } = useNewsData(newsType, categorySlug, locationSlug, sortBy, pageSize);

    // Use infinite scroll if enabled
    const infiniteQuery = useInfiniteScroll ? (
      categorySlug ?
        useInfiniteNewsByCategory(categorySlug, {
          perPage: pageSize,
          locationSlug: locationSlug || undefined,
          sortBy
        }) :
        useInfiniteNews(newsType === "mostsee" ? "mostSee" : newsType, {
          perPage: pageSize,
          locationSlug: locationSlug || undefined
        })
    ) : null;

    // Debug logging
    console.log("DynamicNewsSection render:", {
      title,
      newsType,
      categorySlug,
      layout,
      variant,
      newsCount: news?.length || 0,
      isLoading,
      isError,
      error: error instanceof Error ? error.message : String(error || ""),
    });

    // Apply maxItems limit
    const displayNews = useMemo(() => {
      const items = maxItems ? news.slice(0, maxItems) : news;
      return items;
    }, [news, maxItems]);

    // If using infinite scroll, render InfiniteNewsGrid
    if (useInfiniteScroll && infiniteQuery) {
      return (
        <div className={`space-y-4 ${className}`}>
          {title && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {badge && (
                  <span
                    className={`w-fit px-2 py-1 text-xs text-white font-bold rounded ${badge.color}`}
                  >
                    {badge.text}
                  </span>
                )}
                <div className="flex items-center gap-3 order-first">
                  {icon && <span className="text-2xl">{icon}</span>}
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {title}
                  </h2>
                </div>
              </div>
              {showViewAll && (
                <SmartLink
                  href={categorySlug ? `/category/${categorySlug}` : "/"}
                  className="text-red-600 hover:text-red-700 font-medium text-sm lg:text-base"
                >
                  Lihat Semua →
                </SmartLink>
              )}
            </div>
          )}

          <InfiniteNewsGrid
            data={infiniteQuery.data}
            fetchNextPage={infiniteQuery.fetchNextPage}
            hasNextPage={infiniteQuery.hasNextPage}
            isFetchingNextPage={infiniteQuery.isFetchingNextPage}
            isLoading={infiniteQuery.isLoading}
            isError={infiniteQuery.isError}
            error={infiniteQuery.error}
            className=""
            layout={resolvedLayout === "carousel" || resolvedLayout === "list" ? "double" : resolvedLayout}
            cardVariant={resolvedCardVariant}
            autoLoad={autoLoadOnScroll}
            showLoadMore={showLoadMoreButton}
          />
        </div>
      );
    }

    // Loading state with improved skeleton
    if (isLoading) {
      return (
        <div className={`space-y-4 ${className}`}>
          {title && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {badge && (
                  <span
                    className={`w-fit px-2 py-1 text-xs text-white font-bold rounded ${badge.color}`}
                  >
                    {badge.text}
                  </span>
                )}
                <div className="flex items-center gap-3 order-first">
                  {icon && <span className="text-2xl">{icon}</span>}
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {title}
                  </h2>
                </div>
              </div>
            </div>
          )}
          {Array.from({ length: Math.min(maxItems || 4, 8) }).map(
            (_, index) => (
              <div key={`skeleton-${index}`} className="animate-pulse">
                <div className="flex space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-24 h-16 bg-gray-300 dark:bg-gray-600 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      );
    }

    // Enhanced error state
    if (isError) {
      const errorMessage =
        error instanceof Error ? error.message : "Terjadi kesalahan";
      const isServiceUnavailable =
        errorMessage.includes("503") || errorMessage.includes("unavailable");

      return (
        <div className={className}>
          {title && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {badge && (
                  <span
                    className={`w-fit px-2 py-1 text-xs text-white font-bold rounded ${badge.color}`}
                  >
                    {badge.text}
                  </span>
                )}
                <div className="flex items-center gap-3 order-first">
                  {icon && <span className="text-2xl">{icon}</span>}
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {title}
                  </h2>
                </div>
              </div>
            </div>
          )}
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex flex-col items-center space-y-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div>
                <p className="text-red-500 dark:text-red-400 mb-2 font-medium">
                  {isServiceUnavailable
                    ? "Service Temporarily Unavailable"
                    : "Failed to Load Content"}
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  {isServiceUnavailable
                    ? "Our servers are experiencing high traffic. Please try again in a few moments."
                    : errorMessage}
                </p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-[#de0b66] text-white rounded-lg hover:bg-[#b8094d] transition-colors font-medium"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Enhanced empty state - return null to hide the entire section when no content
    if (!news?.length) {
      // Don't render anything if there's no content - this will hide the entire section
      return null;
    }

    const viewAllLink = categorySlug ? `/category/${categorySlug}` : "/";

    // Render news items based on variant and layout
    const renderNewsItems = () => {
      // If carousel layout is specified, use NewsCarousel
      if (resolvedLayout === "carousel") {
        console.log("Rendering carousel with:", {
          newsCount: displayNews.length,
          carouselOptions,
        });

        if (displayNews.length === 0) {
          return null; // Don't render anything if no news
        }

        return (
          <SimpleNewsCarousel
            news={displayNews}
            variant={resolvedCardVariant}
            itemsPerView={carouselOptions.itemsPerView || 3}
            autoPlay={carouselOptions.autoPlay}
            autoPlayDelay={carouselOptions.autoPlayDelay}
            showArrows={carouselOptions.showArrows}
            loop={carouselOptions.infinite}
            className="w-full"
          />
        );
      }

      // Grid/list layouts based on resolved layout
      const getGridClasses = () => {
        switch (resolvedLayout) {
          case "single":
            return "grid grid-cols-1 gap-6";
          case "double":
            return "grid grid-cols-1 md:grid-cols-2 gap-6";
          case "triple":
            return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6";
          case "quad":
            return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6";
          case "list":
            return "space-y-4 -ml-4";
          default:
            return "grid grid-cols-1 md:grid-cols-2 gap-6";
        }
      };

      const containerClass = getGridClasses();

      return (
        <div className={containerClass}>
          {displayNews.map((newsItem: News) => (
            <NewsErrorBoundary
              key={`${resolvedLayout}-boundary-${newsItem.id}`}
              title="Article"
            >
              <LazyImageArticleCard
                key={`${resolvedLayout}-${newsItem.id}`}
                news={newsItem}
                variant={resolvedCardVariant}
                showViews={true}
                showReactions={true}
                prefetchStrategy="viewport"
              />
            </NewsErrorBoundary>
          ))}
        </div>
      );
    };

    return (
      <NewsErrorBoundary title={title || "News Section"}>
        <div className={className}>
          {/* Section Header */}
          {title && (
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {badge && (
                  <span
                    className={`w-fit px-2 py-1 text-xs text-white font-bold rounded ${badge.color}`}
                  >
                    {badge.text}
                  </span>
                )}
                <div className="flex items-center gap-3 order-first">
                  {icon && <span className="text-2xl">{icon}</span>}
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                    {title}
                  </h2>
                </div>
              </div>

              {showViewAll && (
                <SmartLink
                  href={viewAllLink}
                  prefetchStrategy="hover"
                  className="flex items-center gap-1 text-[#de0b66] hover:text-[#b8094d] dark:text-pink-300 dark:hover:text-pink-400 font-medium text-sm transition-colors group"
                >
                  Lihat Semua
                  <span className="ml-1">→</span>
                </SmartLink>
              )}
            </div>
          )}

          {/* News Items */}
          {renderNewsItems()}

          {/* End indicator - only show when enabled */}
          {showEndIndicator && !isLoading && displayNews.length > 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                Anda telah melihat semua berita yang tersedia
              </p>
              {/*<p className="text-sm text-gray-400 mt-2">
                Total: {displayNews.length.toLocaleString("id-ID")} berita
              </p>*/}
            </div>
          )}
        </div>
      </NewsErrorBoundary>
    );
  },
);

DynamicNewsSection.displayName = "DynamicNewsSection";

export default DynamicNewsSection;
